/**
 * Referral shared helpers — input validation + the visual mapping of the link status (LienStatut) and
 * the return-delivery status (RetourStatut) to a Badge tone + glyph + i18n label key.
 *
 * Every status chip combines COLOR + GLYPH + TEXT (daltonisme + médico-légal); we never rely on color
 * alone (the Badge primitive injects the glyph for clinical tones, and we pass an explicit glyph for the
 * operational success/warn/danger/neutral tones used here).
 */
import type { BadgeTone, SelectOption } from "@/ui";
import type { LienStatut, MedecinCard, RetourStatut } from "./types";

/** Trim + lightweight RFC-ish email check (the server re-validates with zod .email()). */
export function isValidEmail(value: string): boolean {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Parse an editable percentage string ("12" or "12.5") into a number in [0,100], or null if invalid.
 * Empty string → undefined (the field is optional — falls back to the lien's % server-side).
 */
export function parsePct(input: string): number | null | undefined {
  const trimmed = input.trim().replace(",", ".");
  if (trimmed === "") return undefined;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const v = Number.parseFloat(trimmed);
  return Number.isFinite(v) && v >= 0 && v <= 100 ? v : null;
}

/** Format a percentage for display (mono island handles LTR). "—" when absent. */
export function fmtPct(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return "—";
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)} %`;
}

/** Médecin <Select> options: "Nom — email", sorted by name. */
export function medecinOptions(cards: ReadonlyArray<MedecinCard>): SelectOption[] {
  return [...cards]
    .sort((a, b) => a.nom.localeCompare(b.nom))
    .map((c) => ({ value: c.medecin_id, label: `${c.nom} — ${c.novera_email}` }));
}

/** Find an analytics card by médecin id. */
export function findCard(
  cards: ReadonlyArray<MedecinCard>,
  medecinId: string | null | undefined,
): MedecinCard | undefined {
  if (!medecinId) return undefined;
  return cards.find((c) => c.medecin_id === medecinId);
}

/** A status chip descriptor — tone + glyph (shape carries meaning, not color alone) + i18n key. */
export interface StatusVisual {
  tone: BadgeTone;
  glyph: string;
  labelKey: string;
}

const LIEN_VISUAL: Record<LienStatut, StatusVisual> = {
  invite: { tone: "info", glyph: "◔", labelKey: "referral.lien.statut.invite" },
  approuve: { tone: "success", glyph: "●", labelKey: "referral.lien.statut.approuve" },
  rejete: { tone: "danger", glyph: "■", labelKey: "referral.lien.statut.rejete" },
  revoque: { tone: "neutral", glyph: "○", labelKey: "referral.lien.statut.revoque" },
};

/** Visual for a link status; `null` (no link yet) maps to a distinct "aucun lien" neutral chip. */
export function lienVisual(statut: LienStatut | null): StatusVisual {
  if (statut == null) return { tone: "neutral", glyph: "–", labelKey: "referral.lien.statut.none" };
  return LIEN_VISUAL[statut] ?? LIEN_VISUAL.invite;
}

const RETOUR_VISUAL: Record<RetourStatut, StatusVisual> = {
  en_attente: { tone: "warn", glyph: "▲", labelKey: "referral.retour.statut.en_attente" },
  envoye: { tone: "success", glyph: "●", labelKey: "referral.retour.statut.envoye" },
  echec: { tone: "danger", glyph: "■", labelKey: "referral.retour.statut.echec" },
};

export function retourVisual(statut: string): StatusVisual {
  return RETOUR_VISUAL[statut as RetourStatut] ?? RETOUR_VISUAL.en_attente;
}

/** Only an `approuve` link may receive a code / be revoked / accrue returns. */
export function isLinkLive(statut: LienStatut | null): boolean {
  return statut === "approuve";
}
